// Copyright ©️ 2026 eVolve MEP, LLC

import type { StrongOmit, WithOptional } from '../_helpers/helper-types';

import type { AssignedCustomProperty, UpsertCustomProperty } from '../CustomProperty/types';
import type { FacilityId } from '../Facility/types';
import type { Label, LabelId } from '../Label/types';
import type { DateOnly, ISOTimestamp } from '../Misc/dateOnly';
import type { HexTriplet } from '../Misc/types';
import type {
  Manufacturer,
  ManufacturerId,
  PartCatalogId,
  PartCategoryId,
  PartDecision,
  PartId,
  PartTypeId,
  UnitOfMeasure,
  UnitOfMeasureId,
} from '../PartCatalog/types';
import type { ProjectId } from '../Project/types';
import type { Shipment, ShipmentItem } from '../Shipment/types';
import type { TaskId } from '../Tasks/types';
import type { RenderableUser, UserId } from '../User/types';

// The order of the states here determines the display order in various areas of the UI; for example, the Kanban lane order.
export const workOrderStateNames = [
  'Draft',
  'Rejected',
  'Canceled',
  'Submitted',
  'Approved',
  'Transferred',
  'Not Started',
  'In Progress',
  'Blocked',
  'Request for Cancel',
  'Completed',
] as const;
export type WorkOrderStateName = (typeof workOrderStateNames)[number];

declare const workOrderStateIdSymbol: unique symbol;
export type WorkOrderStateId = string & { [workOrderStateIdSymbol]: never };

export type WorkOrderState = {
  workOrderStateId: WorkOrderStateId;
  name: WorkOrderStateName;
};

declare const workOrderStatusIdSymbol: unique symbol;
export type WorkOrderStatusId = string & { [workOrderStatusIdSymbol]: never };

export type WorkOrderStatus = {
  workOrderStatusId: WorkOrderStatusId;
  name: string;
  color: HexTriplet;
  workOrderStateId: WorkOrderStateId;
};

export type CreateWorkOrderStatusBody = {
  workOrderStateId: WorkOrderStateId;
  name: string;
  color: HexTriplet;
};

export type UpdateWorkOrderStatusBody = {
  name: string;
  color: HexTriplet;
};

declare const workOrderIdSymbol: unique symbol;
export type WorkOrderId = string & { [workOrderIdSymbol]: never };

export type WorkOrderStatusHistory = {
  workOrderStateId: WorkOrderStateId;
  workOrderStateName: WorkOrderStateName;
  workOrderStatusId?: WorkOrderStatusId;
  // Only on GET by ID
  workOrderStateComments?: string;
  // Only on GET by ID
  workOrderStateCreatedBy?: RenderableUser;
  /** These fields exist on the response;
   * however, instead of using them,
   * you should read from useWorkOrderStatuses
   */
  // workOrderStatusName?: string;
  // workOrderStatusColor?: HexTriplet;
};

// FIXME: This contract doesn't align with the corresponding fields on WorkOrder, update later
export type WorkOrderStatusHistoryItem = WorkOrderStatusHistory & {
  createdOn: ISOTimestamp;
  createdByUser: RenderableUser; // UserId | null;
  comments: string;
};

export type WorkOrderPaginated = {
  workOrderId: WorkOrderId;
  name: string;
  identifier: string;
  description: string;
  needByDate: DateOnly | null;
  onSiteDate?: DateOnly | null;
  projectId: ProjectId;
  projectName: string;
  facilityId: FacilityId;
  facilityName: string;
  createdByUser: RenderableUser;
  createdOn: ISOTimestamp;
  assignees: RenderableUser[];
  productionCompletedOn?: ISOTimestamp;
  totalAttachmentCount: number;
  totalTimeInSeconds?: number;
  estimatedTimeInSeconds: number;
  labels: Label[];
  customProperties: AssignedCustomProperty[];
} & StrongOmit<WorkOrderStatusHistory, 'workOrderStateComments' | 'workOrderStateCreatedBy'>;

export type WorkOrder = WorkOrderPaginated & {
  productionStartedOn?: ISOTimestamp;
  workOrderAttachmentCount: number;
  /**
   * The sum of all tasks associated with work order items on this work order, multiplied by their respective quantities.
   * Tasks with a `null` estimated unit time are treated as zero.
   **/
  totalEstimatedTaskTimeInSeconds: number;
} & WorkOrderStatusHistory;

export const isWorkOrder = (workOrder: WorkOrder | WorkOrderPaginated): workOrder is WorkOrder =>
  'workOrderAttachmentCount' in workOrder;

/** A work order whose state will change as a result of a pending action, and the state it will end up in. */
export type WorkOrderWithStateChange = Pick<
  WorkOrderPaginated,
  'workOrderId' | 'name' | 'identifier' | 'workOrderStateId' | 'workOrderStateName'
> & {
  newWorkOrderStateId: WorkOrderStateId;
  newWorkOrderStateName: WorkOrderStateName;
};

export type CreateWorkOrderBody = {
  projectId: ProjectId;
  facilityId: FacilityId | null;
  needByDate?: DateOnly;
  name: string;
  description?: string;
};

export type UpdateWorkOrderBody = {
  name?: string | null;
  identifier?: string | null;
  description?: string | null;
  needByDate?: DateOnly | null;
  onSiteDate?: DateOnly | null;
  labels?: LabelId[];
  assignees?: UserId[];
  estimatedTimeInSeconds?: number | null;
  customProperties?: UpsertCustomProperty[];
  facilityId?: FacilityId;
  workOrderStatusId?: WorkOrderStatusId | null;
};

export type WorkOrderKanban = Pick<
  WorkOrderPaginated,
  | 'workOrderId'
  | 'name'
  | 'description'
  | 'customProperties'
  | 'needByDate'
  | 'workOrderStateName'
  | 'workOrderStateId'
  | 'workOrderStatusId'
  | 'facilityName'
  | 'projectName'
> & {
  sortOrder: number;
};

declare const workOrderItemIdSymbol: unique symbol;
export type WorkOrderItemId = string & { [workOrderItemIdSymbol]: never };

export type WorkOrderItem = {
  workOrderItemId: WorkOrderItemId;
  workOrderId: WorkOrderId;
  parentWorkOrderItemId?: WorkOrderItemId;
  name: string;
  description: string | null;
  quantity: number;
  totalQuantity: number;
  spoolQuantity: number | null;
  partId?: PartId | null;
  partTypeId?: PartTypeId | null;
  unitOfMeasureId: UnitOfMeasureId;
  manufacturerId: ManufacturerId;
  manufacturerCatalogNumber: string;
  itemType: BOMItemType;
  hasShopTask: boolean;
  anyTaskStarted: boolean;
  allTasksCompleted: boolean;
  billOfMaterialsItemCount: number;
  hasFabricationElements: boolean;
  anyFabricationElementsMarkedDone: boolean;
  allFabricationElementsMarkedDone: boolean;
  anyFabricationElementBendValidationFailed: boolean;
  /** The sum of all tasks's unit times, multiplied by this item's quantity. */
  totalEstimatedTaskTimeInSeconds: number;
  /** The sum of the item's tasks' task time in seconds, as of when the item was fetched. */
  taskTimeInSeconds: number;
  /** The sum of allocated quantities across all non-deleted shipment items referencing this work order item. */
  totalAllocatedQuantity: number;
  /** The sum of allocated quantities across shipment items whose shipment has been shipped or delivered. */
  totalShippedQuantity: number;
  /** The number of file and web link attachments on the work order item. */
  attachmentCount: number;
  /**
   * Whether this item's quantity has been changed from the quantity a spool publish sets, or any of its
   * spool-generated bill of materials items has had its quantity changed from the quantity Revit published.
   * A model update published from Revit overwrites those edits.
   */
  spoolHasManualEdits: boolean;
};

export type WorkOrderItemShipment = Pick<ShipmentItem, 'shipmentItemId' | 'shipmentId' | 'allocatedQuantity'> &
  Pick<Shipment, 'shipmentStateId' | 'shipmentStateName' | 'shipmentStatusId'> & {
    shipmentName: Shipment['name'];
    shipmentIdentifier: Shipment['identifier'];
  };

/** Every field is optional: the item endpoint patches only the fields present on the body. */
export type UpdateWorkOrderItemBody = {
  name?: string;
  quantity?: number;
  unitOfMeasureId?: UnitOfMeasureId;
  manufacturerId?: ManufacturerId | null;
  manufacturerCatalogNumber?: string | null;
};

export type CreateWorkOrderWriteInItemBody = UpdateWorkOrderItemBody & {
  workOrderId: WorkOrderId;
  parentWorkOrderItemId?: WorkOrderItemId;
};

export type AddCatalogItemToWorkOrderBody = WithOptional<PartDecision, 'decisions'> & {
  quantity: number;
  workOrderId: WorkOrderId;
  parentWorkOrderItemId?: WorkOrderItemId;
};

export type GlobalAggregatedBOMItem = {
  rowId: BomItemRowId;
  item: string;
  unitOfMeasure: UnitOfMeasure['name'];
  quantity: number;
  manufacturer: Manufacturer['name'];
  catalogNumber: string;
  category: string;
};

export type WorkOrderAggregatedBOMItem = {
  rowId: BomItemRowId;
  item: string;
  spoolQuantity: number | null;
  quantityRequired: number;
  unitOfMeasure: string;
  unitQuantity: number;
  manufacturer: string;
  catalogNumber: string;
  itemType: BOMItemType;
};

export type SelectableCategory = {
  category: string;
  categoryId: PartCategoryId;
  catalog: string;
  catalogId: PartCatalogId;
};

export type NonSpoolItemType = 'Part' | 'PC Assembly' | 'Write-In';
type SpoolItemType = 'Spool';
export type BOMItemType = NonSpoolItemType | SpoolItemType;

declare const bomItemSymbol: unique symbol;
export type BomItemRowId = string & { [bomItemSymbol]: never };

export type BlockWorkOrderBody = {
  taskActions: {
    taskId: TaskId;
    saveProgress: boolean;
  }[];
};
